import React, { ChangeEvent, FC, useEffect, useMemo, useRef, useState }             from 'react';
import { v4 as uuid }                                                               from 'uuid';
import { DropdownItemProps, Form, Icon, InputOnChangeData, Table }                  from 'semantic-ui-react';
import { BasicDialog }                                                              from '@src/views/components/modals/BasicDialog';
import { BasicDialogContainer }                                                     from '@src/views/components/modals/BasicDialogContainer';
import { Align, SemanticUIColor, Size }                                             from '@src/types';
import { useCentralCalls }                                                          from '@src/rest/CentralCalls';

interface ConsultantAdminControlsProps {
    onClose: () => void;
    onClientCreated?: () => Promise<void> | void;
}

interface AccountRow {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    emailAddress?: string | null;
}

interface ConsultantRow {
    accountId?: string | null;
    AccountId?: string | null;
}

type ApiRow = Record<string, unknown>;

interface DocumentTypeItem {
    id: string;
    name: string;
}

interface PracticeItem {
    id: string;
    name: string;
}

interface OfferingItem {
    id: string;
    name: string;
    practiceId: string;
}

const ALLOWED_ACCOUNT_EMAIL_DOMAINS = [ 'corval.io', 'nemetzgroup.com' ];

const isAllowedAccountEmail = ( email: string ): boolean => {
    const normalized = email.trim().toLowerCase();
    return ALLOWED_ACCOUNT_EMAIL_DOMAINS.some( domain => normalized.endsWith( `@${ domain }` ) );
};

const consultantAccountId = ( row: ConsultantRow ): string =>
    String( row.accountId ?? row.AccountId ?? '' ).trim().toLowerCase();

const getCaseInsensitiveValue = <T = unknown>( row: ApiRow, key: string ): T | undefined => {
    const actualKey = Object.keys( row ).find( k => k.toLowerCase() === key.toLowerCase() );
    return actualKey ? ( row[ actualKey ] as T ) : undefined;
};

const isTrueLike = ( value: unknown ): boolean =>
    value === true || value === 1 || value === '1' || String( value ).toLowerCase() === 'true';

const normalizeDocumentTypes = ( rows: ApiRow[] ): DocumentTypeItem[] =>
    ( rows ?? [] )
        .map( row => {
            const id = String( getCaseInsensitiveValue( row, 'id' ) ?? '' ).trim();
            const name = String( getCaseInsensitiveValue( row, 'name' ) ?? '' ).trim();
            const offeringLevel = getCaseInsensitiveValue( row, 'offeringLevel' );

            if( !id || !name || !isTrueLike( offeringLevel ) ) return null;

            return { id, name };
        } )
        .filter( ( row ): row is DocumentTypeItem => row != null )
        .sort( ( a, b ) => a.name.localeCompare( b.name ) );

const normalizePractices = ( rows: ApiRow[] ): PracticeItem[] =>
    ( rows ?? [] )
        .map( row => {
            const id = String( getCaseInsensitiveValue( row, 'id' ) ?? '' ).trim();
            const name = String( getCaseInsensitiveValue( row, 'name' ) ?? '' ).trim();

            if( !id || !name ) return null;

            return { id, name };
        } )
        .filter( ( row ): row is PracticeItem => row != null )
        .sort( ( a, b ) => a.name.localeCompare( b.name ) );

const normalizeOfferings = ( rows: ApiRow[] ): OfferingItem[] =>
    ( rows ?? [] )
        .map( row => {
            const id = String( getCaseInsensitiveValue( row, 'id' ) ?? '' ).trim();
            const name = String( getCaseInsensitiveValue( row, 'name' ) ?? '' ).trim();
            const practiceId = String( getCaseInsensitiveValue( row, 'practiceId' ) ?? '' ).trim();

            if( !id || !name ) return null;

            return { id, name, practiceId };
        } )
        .filter( ( row ): row is OfferingItem => row != null )
        .sort( ( a, b ) => a.name.localeCompare( b.name ) );

const inputValue = ( data: InputOnChangeData ): string =>
    data.value !== undefined && data.value !== null ? String( data.value ) : '';

const ConsultantAdminControls: FC<ConsultantAdminControlsProps> = ( {
    onClose,
    onClientCreated,
} ) => {

    const { RunInsert, RunUpdate, TableSelect, uploadPayrollHoursCsv } = useCentralCalls();

    const hoursFileInputRef = useRef<HTMLInputElement>( null );

    const [ clientName, setClientName ]                         = useState( '' ),
          [ clientCode, setClientCode ]                         = useState( '' ),
          [ accounts, setAccounts ]                             = useState<AccountRow[]>( [] ),
          [ selectedAccountId, setSelectedAccountId ]           = useState( '' ),
          [ consultantAccessLevel, setConsultantAccessLevel ]   = useState('Consultant'),
          [ isLoadingAccounts, setIsLoadingAccounts ]           = useState( false ),
          [ isLoadingDocumentTypes, setIsLoadingDocumentTypes ] = useState( false ),
          [ isLoadingPractices, setIsLoadingPractices ]         = useState( false ),
          [ isLoadingOfferings, setIsLoadingOfferings ]         = useState( false ),
          [ documentTypes, setDocumentTypes ]                   = useState<DocumentTypeItem[]>( [] ),
          [ practices, setPractices ]                           = useState<PracticeItem[]>( [] ),
          [ offerings, setOfferings ]                           = useState<OfferingItem[]>( [] ),
          [ documentTypeDrafts, setDocumentTypeDrafts ]         = useState<Record<string, string>>( {} ),
          [ offeringDrafts, setOfferingDrafts ]                 = useState<Record<string, { name: string; practiceId: string }>>( {} ),
          [ newDocumentTypeName, setNewDocumentTypeName ]       = useState( '' ),
          [ offeringName, setOfferingName ]                     = useState( '' ),
          [ offeringDescription, setOfferingDescription ]       = useState( '' ),
          [ offeringPracticeId, setOfferingPracticeId ]         = useState( '' ),
          [ error, setError ]                                   = useState( '' ),
          [ isSaving, setIsSaving ]                             = useState( false ),
          [ isSavingDocumentType, setIsSavingDocumentType ]     = useState( false ),
          [ isSavingOffering, setIsSavingOffering ]             = useState( false ),
          [ updatingOfferingId, setUpdatingOfferingId ]         = useState( '' ),
          [ isUploadingHours, setIsUploadingHours ]             = useState( false ),
          [ hoursUploadError, setHoursUploadError ]             = useState( '' ),
          [ hoursUploadMessage, setHoursUploadMessage ]         = useState( '' );

    const loadDocumentTypes = async ( cancelled: boolean ) => {
        try {
            setIsLoadingDocumentTypes( true );
            const rows = await TableSelect( 'TngDocumentTypes', { OfferingLevel: 1 } ) as ApiRow[];
            if( cancelled ) return;

            const normalized = normalizeDocumentTypes( rows );
            setDocumentTypes( normalized );
            setDocumentTypeDrafts( prev => {
                const next: Record<string, string> = {};
                normalized.forEach( item => {
                    next[ item.id ] = prev[ item.id ] ?? item.name;
                } );
                return next;
            } );
        } catch( err ){
            if( cancelled ) return;
            console.error( '[ConsultantAdminControls] failed to load DocumentTypes:', err );
            setError( 'Could not load document types.' );
        } finally {
            if( !cancelled ) setIsLoadingDocumentTypes( false );
        }
    };

    const loadPractices = async ( cancelled: boolean ) => {
        try {
            setIsLoadingPractices( true );
            const rows = await TableSelect( 'Practices', {} ) as ApiRow[];
            if( cancelled ) return;

            const normalized = normalizePractices( rows );
            setPractices( normalized );
            setOfferingPracticeId( prev => prev || normalized[ 0 ]?.id || '' );
        } catch( err ){
            if( cancelled ) return;
            console.error( '[ConsultantAdminControls] failed to load Practices:', err );
            setError( 'Could not load practices.' );
        } finally {
            if( !cancelled ) setIsLoadingPractices( false );
        }
    };

    const loadOfferings = async ( cancelled: boolean ) => {
        try {
            setIsLoadingOfferings( true );
            const rows = await TableSelect( 'Offerings', {} ) as ApiRow[];
            if( cancelled ) return;

            const normalized = normalizeOfferings( rows );
            setOfferings( normalized );
            setOfferingDrafts( prev => {
                const next: Record<string, { name: string; practiceId: string }> = {};
                normalized.forEach( item => {
                    next[ item.id ] = prev[ item.id ] ?? {
                        name: item.name,
                        practiceId: item.practiceId,
                    };
                } );
                return next;
            } );
        } catch( err ){
            if( cancelled ) return;
            console.error( '[ConsultantAdminControls] failed to load Offerings:', err );
            setError( 'Could not load offerings.' );
        } finally {
            if( !cancelled ) setIsLoadingOfferings( false );
        }
    };

    useEffect( () => {
        let cancelled = false;

        const loadAccounts = async () => {
            try {
                setIsLoadingAccounts( true );
                const [ accountRows, consultantRows ] = await Promise.all( [
                    TableSelect( 'Accounts', {} ) as Promise<AccountRow[]>,
                    TableSelect( 'Consultants', {} ) as Promise<ConsultantRow[]>,
                ] );
                if( cancelled ) return;

                const usedAccountIds = new Set(
                    ( consultantRows ?? [] )
                        .map( consultantAccountId )
                        .filter( id => id.length > 0 )
                );

                const normalized = ( accountRows ?? [] ).filter( row => {
                    if( !row?.id ) return false;
                    const email = ( row.emailAddress ?? '' ).trim();
                    if( !email || !isAllowedAccountEmail( email ) ) return false;
                    return !usedAccountIds.has( row.id.trim().toLowerCase() );
                } );
                setAccounts( normalized );
                setSelectedAccountId( prev =>
                    prev && normalized.some( a => a.id === prev ) ? prev : ( normalized[ 0 ]?.id ?? '' )
                );

                await Promise.all( [
                    loadDocumentTypes( cancelled ),
                    loadPractices( cancelled ),
                    loadOfferings( cancelled ),
                ] );
            } catch( err ){
                if( cancelled ) return;
                console.error( '[ConsultantAdminControls] failed to load Accounts:', err );
                setError( 'Could not load accounts for consultant creation.' );
            } finally {
                if( !cancelled ) setIsLoadingAccounts( false );
            }
        };

        void loadAccounts();
        return () => { cancelled = true; };
        // Intentionally run once on mount; TableSelect identity is unstable across renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [] );

    const accountOptions: DropdownItemProps[] = useMemo( () =>
        accounts.map( account => {
            const first = ( account.firstName ?? '' ).trim();
            const last = ( account.lastName ?? '' ).trim();
            const fullName = `${ first } ${ last }`.trim();
            const email = ( account.emailAddress ?? '' ).trim();
            const label = fullName || email || account.id;
            const description = fullName && email ? email : undefined;

            return {
                key: account.id,
                value: account.id,
                text: label,
                description,
            };
        } ),
    [ accounts ] );

    const handleAddClient = async () => {
        try {
            setIsSaving( true );
            setError( '' );

            const trimmed = clientName.trim();
            if( !trimmed ){
                setError( 'Client name is required.' );
                return;
            }

            await RunInsert( 'Clients', {
                Name: trimmed,
                ClientCode: clientCode.trim() || null,
            } );

            await onClientCreated?.();
            onClose();
        } catch( err ){
            const message = err instanceof Error ? err.message : 'Failed to create client.';
            setError( message );
        } finally {
            setIsSaving( false );
        }
    };

    const handleAddConsultant = async () => {
        try {
            setIsSaving( true );
            setError( '' );

            if( !selectedAccountId ){
                setError( 'Select an account.' );
                return;
            }

            await RunInsert( 'Consultants', {
                AccountId: selectedAccountId,
                AccessLevel: consultantAccessLevel.trim() || null,
            } );

            await onClientCreated?.();
            onClose();
        } catch( err ){
            const message = err instanceof Error ? err.message : 'Failed to create consultant.';
            setError( message );
        } finally {
            setIsSaving( false );
        }
    };

    const handleHoursFileSelected = async ( event: ChangeEvent<HTMLInputElement> ) => {
        const file = event.target.files?.[ 0 ];
        // Reset so picking the same file again still fires onChange.
        event.target.value = '';
        if( !file ) return;

        setHoursUploadError( '' );
        setHoursUploadMessage( '' );

        if( !/\.(xlsx|csv)$/i.test( file.name ) ){
            setHoursUploadError( 'Choose an .xlsx or .csv file.' );
            return;
        }

        try {
            setIsUploadingHours( true );
            const result = await uploadPayrollHoursCsv( file );
            const rows = ( count: number | undefined ) => `${ count ?? 0 } row${ count === 1 ? '' : 's' }`;
            setHoursUploadMessage(
                `${ file.name }: ${ rows( result.rowsInserted ) } added, `
                + `${ rows( result.rowsUpdated ) } updated, ${ rows( result.rowsUnchanged ) } unchanged`
                + ( result.duplicatesSkipped ? `, ${ rows( result.duplicatesSkipped ) } skipped as duplicates.` : '.' )
            );
        } catch( err ){
            const message = err instanceof Error ? err.message : 'Failed to upload consultant hours.';
            setHoursUploadError( message );
        } finally {
            setIsUploadingHours( false );
        }
    };

    const handleDocumentTypeDraftChange = ( id: string, value: string ) => {
        setDocumentTypeDrafts( prev => ( {
            ...prev,
            [ id ]: value,
        } ) );
        if( error ) setError( '' );
    };

    const handleRenameDocumentType = async ( documentTypeId: string ) => {
        const draft = String( documentTypeDrafts[ documentTypeId ] ?? '' ).trim();
        const current = documentTypes.find( item => item.id === documentTypeId );
        if( !current ) return;

        if( !draft ){
            setError( 'Document type name is required.' );
            return;
        }

        if( draft === current.name ) return;

        try {
            setIsSavingDocumentType( true );
            setError( '' );

            await RunUpdate( 'TngDocumentTypes', documentTypeId, {
                Name: draft,
            } );

            setDocumentTypes( prev => prev.map( item =>
                item.id === documentTypeId ? { ...item, name: draft } : item
            ) );
        } catch( err ){
            const message = err instanceof Error ? err.message : 'Failed to update document type.';
            setError( message );
        } finally {
            setIsSavingDocumentType( false );
        }
    };

    const handleAddDocumentType = async () => {
        const trimmed = newDocumentTypeName.trim();

        if( !trimmed ){
            setError( 'Document type name is required.' );
            return;
        }

        try {
            setIsSavingDocumentType( true );
            setError( '' );

            await RunInsert( 'TngDocumentTypes', {
                Name: trimmed,
                OfferingLevel: true,
            } );

            setNewDocumentTypeName( '' );
            await loadDocumentTypes( false );
        } catch( err ){
            const message = err instanceof Error ? err.message : 'Failed to create document type.';
            setError( message );
        } finally {
            setIsSavingDocumentType( false );
        }
    };

    const handleAddOffering = async () => {
        const trimmedName = offeringName.trim();

        if( !trimmedName ){
            setError( 'Offering name is required.' );
            return;
        }

        if( !offeringPracticeId ){
            setError( 'Practice is required for an offering.' );
            return;
        }

        try {
            setIsSavingOffering( true );
            setError( '' );

            await RunInsert( 'Offerings', {
                Id: uuid(),
                PracticeId: offeringPracticeId,
                Name: trimmedName,
                Description: offeringDescription.trim() || null,
            } );

            setOfferingName( '' );
            setOfferingDescription( '' );
            await loadOfferings( false );
        } catch( err ){
            const message = err instanceof Error ? err.message : 'Failed to create offering.';
            setError( message );
        } finally {
            setIsSavingOffering( false );
        }
    };

    const handleOfferingDraftChange = ( offeringId: string, set: Partial<{ name: string; practiceId: string }> ) => {
        setOfferingDrafts( prev => {
            const base = prev[ offeringId ] ?? { name: '', practiceId: '' };
            return {
                ...prev,
                [ offeringId ]: {
                    ...base,
                    ...set,
                },
            };
        } );
        if( error ) setError( '' );
    };

    const handleUpdateOffering = async ( offeringId: string ) => {
        const current = offerings.find( offering => offering.id === offeringId );
        if( !current ) return;

        const draft = offeringDrafts[ offeringId ] ?? {
            name: current.name,
            practiceId: current.practiceId,
        };

        const trimmedName = draft.name.trim();
        const trimmedPracticeId = draft.practiceId.trim();

        if( !trimmedName ){
            setError( 'Offering name is required.' );
            return;
        }

        if( !trimmedPracticeId ){
            setError( 'Practice is required for an offering.' );
            return;
        }

        const unchanged =
            trimmedName === current.name
            && trimmedPracticeId === current.practiceId;

        if( unchanged ) return;

        try {
            setUpdatingOfferingId( offeringId );
            setError( '' );

            await RunUpdate( 'Offerings', offeringId, {
                Name: trimmedName,
                PracticeId: trimmedPracticeId,
            } );

            setOfferings( prev =>
                prev
                    .map( offering =>
                        offering.id === offeringId
                            ? {
                                ...offering,
                                name: trimmedName,
                                practiceId: trimmedPracticeId,
                            }
                            : offering
                    )
                    .sort( ( a, b ) => a.name.localeCompare( b.name ) )
            );
        } catch( err ){
            const message = err instanceof Error ? err.message : 'Failed to update offering.';
            setError( message );
        } finally {
            setUpdatingOfferingId( '' );
        }
    };

    const practiceOptions: DropdownItemProps[] = useMemo( () =>
        practices.map( practice => ( {
            key: practice.id,
            value: practice.id,
            text: practice.name,
        } ) ),
    [ practices ] );

    return (
        <BasicDialog
            showCancelButton
            showSubmitButton={ false }
            submitColor={ SemanticUIColor.PURPLE }
            onCancel={ onClose }
            onSubmit={ onClose }
            controlAlign={ Align.CENTER }
            className={ 'no-padding create-client-dialog' }
        >
            <BasicDialogContainer
                title={ 'Consultant Admin Controls' }
                titleColor={ SemanticUIColor.PURPLE }
                contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }
                contentMinHeight={ '28dvh' }
                onClose={ onClose }
            >
                <Form className={ 'create-client-form' }>
                    { error && (
                        <p className={ 'create-client-error' }>
                            <Icon name={ 'warning circle' }/> { error }
                        </p>
                    ) }

                    <div className={ 'create-client-section' }>
                        <div className={ 'create-client-section-header' }>
                            <span>Add Client</span>
                        </div>

                        <Form.Input
                            label={ 'Client name' }
                            placeholder={ 'e.g. Acme Life Sciences' }
                            value={ clientName }
                            required
                            disabled={ isSaving }
                            onChange={ ( _e, data ) => {
                                setClientName( inputValue( data ) );
                                if( error ) setError( '' );
                            } }
                        />
                        <Form.Input
                            label={ 'Client code (optional)' }
                            placeholder={ 'e.g. ACME' }
                            value={ clientCode }
                            maxLength={ 4 }
                            disabled={ isSaving }
                            onChange={ ( _e, data ) => {
                                setClientCode( inputValue( data ) );
                                if( error ) setError( '' );
                            } }
                        />
                        <Form.Button
                            color={ 'purple' }
                            content={ isSaving ? 'Saving...' : 'Add Client' }
                            disabled={ isSaving }
                            onClick={ () => { void handleAddClient(); } }
                        />
                    </div>

                    <div className={ 'create-client-section' }>
                        <div className={ 'create-client-section-header' }>
                            <span>Add Consultant</span>
                        </div>

                        <Form.Select
                            label={ 'Account' }
                            options={ accountOptions }
                            value={ selectedAccountId }
                            loading={ isLoadingAccounts }
                            disabled={ isSaving || isLoadingAccounts || accountOptions.length === 0 }
                            placeholder={ isLoadingAccounts ? 'Loading accounts...' : 'Select account' }
                            search
                            selection
                            onChange={ ( _e, data ) => {
                                setSelectedAccountId( String( data.value ?? '' ) );
                                if( error ) setError( '' );
                            } }
                        />

                        <Form.Input
                            label={ 'Access level (optional)' }
                            placeholder={ 'e.g. Consultant' }
                            value={ consultantAccessLevel }
                            disabled={ isSaving }
                            onChange={ ( _e, data ) => {
                                setConsultantAccessLevel( inputValue( data ) );
                                if( error ) setError( '' );
                            } }
                        />
                        <Form.Button
                            type={ 'button' }
                            color={ 'purple' }
                            content={ isSaving ? 'Saving...' : 'Add Consultant' }
                            disabled={ isSaving || isLoadingAccounts || accountOptions.length === 0 }
                            onClick={ () => { void handleAddConsultant(); } }
                        />
                    </div>

                    <div className={ 'create-client-section' }>
                        <div className={ 'create-client-section-header' }>
                            <span>Upload Consultant Hours</span>
                        </div>

                        <p>Upload the payroll hours export (.xlsx or .csv). Rows already uploaded for the same employee, date, start and end time, client and project are updated; repeats within the file are skipped. The whole file is rejected if any row has an invalid value.</p>

                        { hoursUploadError && (
                            <p className={ 'create-client-error' } style={ { whiteSpace: 'pre-line' } }>
                                <Icon name={ 'warning circle' }/> { hoursUploadError }
                            </p>
                        ) }

                        { hoursUploadMessage && (
                            <p>
                                <Icon name={ 'check circle' } color={ 'green' }/> { hoursUploadMessage }
                            </p>
                        ) }

                        <input
                            ref={ hoursFileInputRef }
                            type={ 'file' }
                            accept={ '.xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv' }
                            style={ { display: 'none' } }
                            onChange={ event => { void handleHoursFileSelected( event ); } }
                        />
                        <Form.Button
                            type={ 'button' }
                            color={ 'purple' }
                            icon={ 'upload' }
                            content={ isUploadingHours ? 'Uploading...' : 'Upload Consultant Hours' }
                            loading={ isUploadingHours }
                            disabled={ isUploadingHours }
                            onClick={ () => hoursFileInputRef.current?.click() }
                        />
                    </div>

                    <div className={ 'create-client-section' }>
                        <div className={ 'create-client-section-header' }>
                            <span>Manage Document Types</span>
                        </div>

                        { isLoadingDocumentTypes && (
                            <p>
                                <Icon name={ 'spinner' } loading/> Loading document types...
                            </p>
                        ) }

                        { !isLoadingDocumentTypes && documentTypes.length === 0 && (
                            <p>No offering-level document types found.</p>
                        ) }

                        { !isLoadingDocumentTypes && documentTypes.map( documentType => {
                            const draft = documentTypeDrafts[ documentType.id ] ?? documentType.name;
                            const isChanged = draft.trim() !== documentType.name;

                            return (
                                <div className={ 'create-client-inline-edit-row' } key={ documentType.id }>
                                    <Form.Input
                                        label={ 'Name' }
                                        value={ draft }
                                        disabled={ isSavingDocumentType }
                                        onChange={ ( _e, data ) => {
                                            handleDocumentTypeDraftChange( documentType.id, inputValue( data ) );
                                        } }
                                    />
                                    <Form.Button
                                        type={ 'button' }
                                        color={ 'purple' }
                                        content={ isSavingDocumentType ? 'Updating...' : 'Update' }
                                        disabled={ isSavingDocumentType || !isChanged || draft.trim().length === 0 }
                                        onClick={ () => { void handleRenameDocumentType( documentType.id ); } }
                                    />
                                </div>
                            );
                        } ) }

                        <Form.Input
                            label={ 'New document type name' }
                            placeholder={ 'e.g. Statement of Work' }
                            value={ newDocumentTypeName }
                            disabled={ isSavingDocumentType }
                            onChange={ ( _e, data ) => {
                                setNewDocumentTypeName( inputValue( data ) );
                                if( error ) setError( '' );
                            } }
                        />

                        <Form.Button
                            type={ 'button' }
                            color={ 'purple' }
                            content={ isSavingDocumentType ? 'Saving...' : 'Add Document Type' }
                            disabled={ isSavingDocumentType || newDocumentTypeName.trim().length === 0 }
                            onClick={ () => { void handleAddDocumentType(); } }
                        />
                    </div>

                    <div className={ 'create-client-section' }>
                        <div className={ 'create-client-section-header' }>
                            <span>Add Offering</span>
                        </div>

                        <Form.Input
                            label={ 'Offering name' }
                            placeholder={ 'e.g. Market Access Workshop' }
                            value={ offeringName }
                            required
                            disabled={ isSavingOffering }
                            onChange={ ( _e, data ) => {
                                setOfferingName( inputValue( data ) );
                                if( error ) setError( '' );
                            } }
                        />

                        <Form.Input
                            label={ 'Description (optional)' }
                            placeholder={ 'Optional description' }
                            value={ offeringDescription }
                            disabled={ isSavingOffering }
                            onChange={ ( _e, data ) => {
                                setOfferingDescription( inputValue( data ) );
                                if( error ) setError( '' );
                            } }
                        />

                        <Form.Select
                            label={ 'Practice' }
                            options={ practiceOptions }
                            value={ offeringPracticeId }
                            loading={ isLoadingPractices }
                            disabled={ isSavingOffering || isLoadingPractices || practiceOptions.length === 0 }
                            placeholder={ isLoadingPractices ? 'Loading practices...' : 'Select practice' }
                            search
                            selection
                            onChange={ ( _e, data ) => {
                                setOfferingPracticeId( String( data.value ?? '' ) );
                                if( error ) setError( '' );
                            } }
                        />

                        <Form.Button
                            type={ 'button' }
                            color={ 'purple' }
                            content={ isSavingOffering ? 'Saving...' : 'Add Offering' }
                            disabled={
                                isSavingOffering
                                || isLoadingPractices
                                || practiceOptions.length === 0
                                || offeringName.trim().length === 0
                            }
                            onClick={ () => { void handleAddOffering(); } }
                        />
                    </div>

                    <div className={ 'create-client-section' }>
                        <div className={ 'create-client-section-header' }>
                            <span>Manage Offerings</span>
                        </div>

                        { isLoadingOfferings && (
                            <p>
                                <Icon name={ 'spinner' } loading/> Loading offerings...
                            </p>
                        ) }

                        { !isLoadingOfferings && offerings.length === 0 && (
                            <p>No offerings found.</p>
                        ) }

                        { !isLoadingOfferings && offerings.length > 0 && (
                            <Table celled compact selectable className={ 'create-client-offerings-table' }>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Offering</Table.HeaderCell>
                                        <Table.HeaderCell>Practice</Table.HeaderCell>
                                        <Table.HeaderCell collapsing>Action</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    { offerings.map( offering => {
                                        const draft = offeringDrafts[ offering.id ] ?? {
                                            name: offering.name,
                                            practiceId: offering.practiceId,
                                        };

                                        const nameChanged = draft.name.trim() !== offering.name;
                                        const practiceChanged = draft.practiceId.trim() !== offering.practiceId;
                                        const isChanged = nameChanged || practiceChanged;
                                        const isUpdating = updatingOfferingId === offering.id;

                                        return (
                                            <Table.Row key={ offering.id }>
                                                <Table.Cell>
                                                    <Form.Input
                                                        label={ false }
                                                        value={ draft.name }
                                                        disabled={ isUpdating }
                                                        placeholder={ 'Offering name' }
                                                        onChange={ ( _e, data ) => {
                                                            handleOfferingDraftChange( offering.id, {
                                                                name: inputValue( data ),
                                                            } );
                                                        } }
                                                    />
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Form.Select
                                                        label={ false }
                                                        options={ practiceOptions }
                                                        value={ draft.practiceId }
                                                        loading={ isLoadingPractices }
                                                        disabled={ isUpdating || isLoadingPractices || practiceOptions.length === 0 }
                                                        placeholder={ 'Select practice' }
                                                        search
                                                        selection
                                                        onChange={ ( _e, data ) => {
                                                            handleOfferingDraftChange( offering.id, {
                                                                practiceId: String( data.value ?? '' ),
                                                            } );
                                                        } }
                                                    />
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Form.Button
                                                        type={ 'button' }
                                                        color={ 'purple' }
                                                        content={ isUpdating ? 'Updating...' : 'Update' }
                                                        disabled={
                                                            isUpdating
                                                            || !isChanged
                                                            || draft.name.trim().length === 0
                                                            || draft.practiceId.trim().length === 0
                                                        }
                                                        onClick={ () => { void handleUpdateOffering( offering.id ); } }
                                                    />
                                                </Table.Cell>
                                            </Table.Row>
                                        );
                                    } ) }
                                </Table.Body>
                            </Table>
                        ) }
                    </div>
                </Form>
            </BasicDialogContainer>
        </BasicDialog>
    );
};

export default ConsultantAdminControls;
